import React, { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { Input, Alert } from "antd";

const libraries = ["places"];

const LocationPicker = ({ value, onChange, onLocationNameChange }) => {
  const [map, setMap] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(
    value?.coordinates || null
  );
  const [locationName, setLocationName] = useState(value?.locationName || "");
  const [geocoder, setGeocoder] = useState(null);
  const autocompleteRef = useRef(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries,
  });

  useEffect(() => {
    if (value?.coordinates) {
      setSelectedLocation(value.coordinates);
    }
    if (value?.locationName) {
      setLocationName(value.locationName);
    }
  }, [value]);

  const defaultCenter = {
    lat: 40.7128,
    lng: -74.006,
  };

  const onMapLoad = useCallback((map) => {
    setMap(map);
    const geocoderInstance = new window.google.maps.Geocoder();
    setGeocoder(geocoderInstance);
  }, []);

  const onMapClick = useCallback(
    async (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const coordinates = { lat, lng };

      setSelectedLocation(coordinates);
      onChange?.(coordinates);

      if (geocoder) {
        geocoder.geocode({ location: coordinates }, (results, status) => {
          if (status === "OK" && results[0]) {
            const name = results[0].formatted_address;
            setLocationName(name);
            onLocationNameChange?.(name);
          }
        });
      }
    },
    [geocoder, onChange, onLocationNameChange]
  );

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const location = place.geometry.location;
        const coordinates = {
          lat: location.lat(),
          lng: location.lng(),
        };
        setSelectedLocation(coordinates);
        onChange?.(coordinates);
        const name = place.formatted_address || place.name;
        setLocationName(name);
        onLocationNameChange?.(name);
        if (map) {
          map.setCenter(coordinates);
          map.setZoom(15);
        }
      }
    }
  }, [map, onChange, onLocationNameChange]);

  const onMarkerDragEnd = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const coordinates = { lat, lng };

      setSelectedLocation(coordinates);
      onChange?.(coordinates);

      if (geocoder) {
        geocoder.geocode({ location: coordinates }, (results, status) => {
          if (status === "OK" && results[0]) {
            const name = results[0].formatted_address;
            setLocationName(name);
            onLocationNameChange?.(name);
          }
        });
      }
    },
    [geocoder, onChange, onLocationNameChange]
  );

  const handleLocationNameChange = (e) => {
    const name = e.target.value;
    setLocationName(name);
    onLocationNameChange?.(name);
  };

  if (loadError) {
    return (
      <Alert
        message="Google Maps Error"
        description="Please add REACT_APP_GOOGLE_MAPS_API_KEY to your environment variables."
        type="error"
      />
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        Loading map...
      </div>
    );
  }

  if (!apiKey) {
    return (
      <Alert
        message="Google Maps API Key Required"
        description="Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file."
        type="warning"
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: "10px" }}>
        <Autocomplete
          onLoad={(autocomplete) => {
            autocompleteRef.current = autocomplete;
          }}
          onPlaceChanged={onPlaceChanged}
          options={{
            types: ["establishment", "geocode"],
          }}
        >
          <Input
            placeholder="Search location..."
            style={{ width: "100%" }}
          />
        </Autocomplete>
      </div>
      <div style={{ marginBottom: "10px" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "400px" }}
          center={selectedLocation || defaultCenter}
          zoom={selectedLocation ? 15 : 10}
          onLoad={onMapLoad}
          onClick={onMapClick}
        >
          {selectedLocation && (
            <Marker
              position={selectedLocation}
              draggable={true}
              onDragEnd={onMarkerDragEnd}
            />
          )}
        </GoogleMap>
      </div>
      <div style={{ marginBottom: "10px" }}>
        <Input
          placeholder="Location Name"
          value={locationName}
          onChange={handleLocationNameChange}
        />
      </div>
      {selectedLocation && (
        <div style={{ fontSize: "12px", color: "#666" }}>
          Coordinates: {selectedLocation.lat.toFixed(6)},{" "}
          {selectedLocation.lng.toFixed(6)}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;

